<template>
  <div class="measure-tile min-h-fit">
    <button ref="tileButtonRef" type="button" class="tile-button" @click="open = !open">
      <div class="tile-header">
        <span class="tile-number">Takt {{ measure.measure_number }}</span>
        <span class="tile-variant" :class="dirClass">{{ selectedLabel }}</span>
        <Icon v-if="!selectedPlayable" icon="fa6-solid:triangle-exclamation" class="text-red-500" title="nicht spielbar"/>
        <span v-if="isModified" class="tile-modified" title="weicht von der Auto-Auswahl ab">●</span>
      </div>
      <OsmdMeasure class="w-full grid" v-if="selectedVariant" :music-xml="selectedVariant.music_xml" :zoom="0.55"/>
      <div v-else class="tile-empty">—</div>
    </button>

    <VariantPickerPopover
        v-if="open"
        :anchor="tileButtonRef"
        :variants="measure.variants ?? []"
        :current="selected"
        @select="onSelect"
        @close="open = false"
    />

  </div>
</template>

<script setup lang="ts">
import {computed, ref} from 'vue';
import {Icon} from '@iconify/vue';
import type {MeasureVariant, MeasureVariants} from '@/generated';
import OsmdMeasure from './OsmdMeasure.vue';
import VariantPickerPopover from './VariantPickerPopover.vue';

const {measure, defaultVariant = ''} = defineProps<{
  measure: MeasureVariants;
  defaultVariant?: string;
}>();

const selected = defineModel<string>('selected', {required: true});

const open = ref(false);
const tileButtonRef = ref<HTMLElement | null>(null);

const selectedVariant = computed<MeasureVariant | undefined>((oldValue) => {
  const found = (measure.variants ?? []).find((v) => v.variant_name === selected.value);
  if (oldValue && found && oldValue.variant_name === found.variant_name && oldValue.music_xml === found.music_xml) {
    return oldValue;
  }
  return found;
});

const selectedLabel = computed(() => selectedVariant.value?.label ?? selected.value);
const selectedPlayable = computed(() => selectedVariant.value?.playable ?? true);
const dirClass = computed(() => (selectedVariant.value?.direction === 'pull' ? 'text-blue-600' : 'text-slate-900'));
const isModified = computed(() => !!defaultVariant && selected.value !== defaultVariant);

function onSelect(variantName: string): void {
  selected.value = variantName;
  open.value = false;
}
</script>

<style scoped>
@import 'tailwindcss';

.measure-tile {
  @apply relative min-h-max;
}

.tile-button {
  @apply flex  w-full flex-col gap-1 rounded-lg border border-slate-200 bg-white p-2 text-left
  shadow-sm transition-all hover:border-blue-400 hover:shadow-md;
}

.tile-header {
  @apply flex items-center gap-1.5 text-xs;
}

.tile-number {
  @apply font-semibold text-slate-500;
}

.tile-variant {
  @apply font-semibold;
}

.tile-modified {
  @apply ml-auto text-blue-500;
}

.tile-empty {
  @apply py-4 text-center text-slate-300;
}
</style>
